import { Link, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  PhoneInput,
  Stack,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useAuthControllerLogin } from '../api/generated/auth/auth';
import { FseApiError, readCurrentRole } from '../api/client';

// Phone is captured as the 9-digit ZA-local portion (PhoneInput renders the
// fixed `+27` adornment). We rebuild the E.164 string at the API boundary so
// the request payload is unchanged. Validation messages are resolved through
// t() inside the component (TD-072), so the schema validates shape only.
const loginSchema = z.object({
  phone: z.string().regex(/^\d{9}$/),
  pin: z.string().regex(/^\d{4,8}$/),
});

function persistTokens(tokens: { access_token: string; refresh_token: string }) {
  window.localStorage.setItem('fse_access_token', tokens.access_token);
  window.localStorage.setItem('fse_refresh_token', tokens.refresh_token);
}

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ phone?: string; pin?: string }>({});

  const loginMutation = useAuthControllerLogin({
    mutation: {
      onSuccess: (tokens) => {
        persistTokens(tokens);
        // Route by role — must mirror router.ts indexRoute.beforeLoad exactly:
        // sysCollab is a kanban-only persona (never the operational app
        // surface); farmer / group_lead land on their self-service /home (the
        // admin /dashboard is server-gated to admin/coop_admin and would be a
        // broken affordance for them); everyone else → /dashboard.
        const role = readCurrentRole();
        if (role === 'sysCollab') {
          window.location.assign('/kanban.html');
        } else if (role === 'farmer' || role === 'group_lead') {
          void navigate({ to: '/home' });
        } else {
          void navigate({ to: '/dashboard' });
        }
      },
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    const parsed = loginSchema.safeParse({ phone, pin });
    if (!parsed.success) {
      const errs: { phone?: string; pin?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'phone') errs.phone = t('login.error.phoneInvalid');
        if (key === 'pin') errs.pin = t('login.error.pinInvalid');
      }
      setFieldErrors(errs);
      return;
    }
    // Rebuild the E.164 phone the API expects from the validated 9 local digits.
    loginMutation.mutate({ data: { phone: `+27${parsed.data.phone}`, pin: parsed.data.pin } });
  }

  const apiError = loginMutation.error as unknown;
  let apiErrorMessage: string | null = null;
  if (apiError instanceof FseApiError) {
    apiErrorMessage =
      apiError.status === 401
        ? t('login.error.invalidCredentials')
        : apiError.status === 400
          ? t('login.error.badRequest')
          : apiError.message;
  } else if (apiError) {
    apiErrorMessage = String(apiError);
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ position: 'fixed', top: 8, right: 8 }}>
        <ColorModeToggle />
      </Box>
      <Container maxWidth="xs">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 1 }}>
              <Text variant="display-small" as="h1" align="center">
                {t('login.appName')}
              </Text>
            </Box>
            <Text variant="body-large" align="center" color="text.secondary">
              {t('login.subtitle')}
            </Text>
          </Box>

          <Card>
            <CardContent>
              <form onSubmit={onSubmit} noValidate>
                <Stack spacing={2}>
                  <PhoneInput
                    value={phone}
                    onChangeText={setPhone}
                    error={fieldErrors.phone}
                    disabled={loginMutation.isPending}
                  />
                  <TextField
                    label={t('login.pinLabel')}
                    type="password"
                    autoComplete="current-password"
                    inputMode="numeric"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    error={!!fieldErrors.pin}
                    helperText={fieldErrors.pin}
                    disabled={loginMutation.isPending}
                  />

                  {apiErrorMessage && (
                    <Alert severity="error" variant="outlined">
                      {apiErrorMessage}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    size="large"
                    fullWidth
                    disabled={loginMutation.isPending}
                  >
                    {loginMutation.isPending ? t('login.signingIn') : t('login.signIn')}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Link to="/forgot-pin" style={{ textDecoration: 'none' }}>
                      <Typography variant="body2" color="primary">
                        {t('login.forgotPin')}
                      </Typography>
                    </Link>
                  </Box>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {/* Onboarding entry point for farmers — coop admins sign in above.
              Claim = a farmer whose account was pre-registered (pin-less) by
              their cooperative. (G1 self-signup is out of pilot scope.) */}
          <Stack spacing={1} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('login.registeredPrompt')}{' '}
              <Link to="/claim" style={{ textDecoration: 'none' }}>
                <Typography component="span" variant="body2" color="primary">
                  {t('login.claimAccount')}
                </Typography>
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
